/**
 * Maryland county-level ELA proficiency — verified 2024-25 MCAP values.
 *
 * Source: Maryland State Department of Education (MSDE) — MCAP SY 2024-2025
 *         released August 26, 2025.
 * Statewide ELA average: 50.8%
 *
 * Update each September when MSDE releases annual MCAP results:
 *   https://marylandpublicschools.org → Assessment → MCAP Results → LEA summary.
 *
 * Keyed by 5-digit FIPS code (matches GEOID in the GeoJSON shipped at
 * /public/maryland-counties.geojson). `name` matches the official Census
 * NAME field minus the trailing " County" suffix.
 */
export const MD_COUNTY_ELA_2025 = {
  "24001": { name: "Allegany",        ela_pct: 53.0 },
  "24003": { name: "Anne Arundel",    ela_pct: 56.3 },
  "24005": { name: "Baltimore",       ela_pct: 45.0 },
  "24009": { name: "Calvert",         ela_pct: 66.2 },
  "24011": { name: "Caroline",        ela_pct: 49.0 },
  "24013": { name: "Carroll",         ela_pct: 66.5 },
  "24015": { name: "Cecil",           ela_pct: 46.3 },
  "24017": { name: "Charles",         ela_pct: 43.8 },
  "24019": { name: "Dorchester",      ela_pct: 41.3 },
  "24021": { name: "Frederick",       ela_pct: 63.0 },
  "24023": { name: "Garrett",         ela_pct: 50.6 },
  "24025": { name: "Harford",         ela_pct: 60.2 },
  "24027": { name: "Howard",          ela_pct: 65.2 },
  "24029": { name: "Kent",            ela_pct: 40.7 },
  "24031": { name: "Montgomery",      ela_pct: 57.0 },
  "24033": { name: "Prince George's", ela_pct: 38.3 },
  "24035": { name: "Queen Anne's",    ela_pct: 57.0 },
  "24037": { name: "Saint Mary's",    ela_pct: 57.3 },
  "24039": { name: "Somerset",        ela_pct: 35.1 },
  "24041": { name: "Talbot",          ela_pct: 45.4 },
  "24043": { name: "Washington",      ela_pct: 54.4 },
  "24045": { name: "Wicomico",        ela_pct: 39.6 },
  "24047": { name: "Worcester",       ela_pct: 68.5 },
  // Baltimore City — independent city, not a county. Label without "County".
  "24510": { name: "Baltimore City",  ela_pct: 31.2, is_city: true },
};

export const MD_STATEWIDE_AVG_PCT = 50.8;

export const MD_ELA_SOURCE = "MD MCAP SY 2024-25";

export const MD_ELA_SOURCE_LONG = "Maryland State Department of Education — MCAP SY 2024-2025 (released Aug 26, 2025)";

/**
 * MD-specific bucket scale (different from DC scale because MD's per-county
 * distribution is wider — 31% to 69% vs DC's 43% to 86%).
 * Darker = lower proficiency.
 */
export function colorForMdProficiency(pct) {
  if (pct == null || Number.isNaN(pct)) return "#F5F5F4";
  if (pct >= 65) return "#FFEDED";
  if (pct >= 55) return "#FFBDBD";
  if (pct >= 45) return "#FF8A8A";
  if (pct >= 35) return "#FF5252";
  return "#C62828";
}

export const MD_ELA_SR_DESCRIPTION =
  "Map showing Maryland county boundaries shaded by 2024-25 MCAP ELA proficiency. " +
  "Darker red indicates lower proficiency. Worcester (68.5%) is highest; " +
  "Baltimore City (31.2%) and Somerset (35.1%) are lowest. " +
  "Prince George's County, where many Bookshelf Project families live, is at 38.3%.";

/**
 * Lookup helper: county name (with or without " County" suffix) → ela_pct.
 * Useful for the admin panel CSV exports and the cross-border insight card.
 */
export function elaPctForCountyName(rawName) {
  if (!rawName) return null;
  const clean = String(rawName).replace(/\s+County$/i, "").trim().toLowerCase();
  for (const v of Object.values(MD_COUNTY_ELA_2025)) {
    if (v.name.toLowerCase() === clean) return v.ela_pct;
  }
  return null;
}
